import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Language as WebIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AssignmentOutlined as FormIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import ConfirmModal from '../../components/ConfirmModal';
import {
  getWebsiteLeadIntegration,
  updateWebsiteLeadIntegration,
  regenerateWebsiteLeadKey,
} from '../../services/websiteLeads';
import { leadService } from '../../services/lead';

const Website = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [totalLeads, setTotalLeads] = useState(0);

  // Integration settings states
  const [integrationData, setIntegrationData] = useState(null);
  const [loadingIntegration, setLoadingIntegration] = useState(false);
  const [savingIntegration, setSavingIntegration] = useState(false);
  
  // Fields for the settings form
  const [isActive, setIsActive] = useState(false);
  const [buttonText, setButtonText] = useState('Enquire Now');
  const [formTitle, setFormTitle] = useState('Request a Call Back');
  const [primaryColor, setPrimaryColor] = useState('#2563eb');
  const [allowedDomainsInput, setAllowedDomainsInput] = useState('');
  
  // Modal & regeneration states
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const fetchWebsiteLeads = async () => {
    setLeadsLoading(true);
    try {
      const data = await leadService.getLeads({
        source: 'Website',
        page: page + 1,
        limit: rowsPerPage,
        filterKey: searchTerm.trim() || undefined,
      });
      if (data) {
        setLeads(data.docs || []);
        setTotalLeads(data.totalDocs || data.total || 0);
      }
    } catch (err) {
      showToast(err.message || 'Failed to load website leads', 'error');
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchWebsiteLeads();
    showToast('Synced website leads cache!', 'success');
  };

  const updateLocalState = (data) => {
    setIntegrationData(data);
    setIsActive(data.isActive ?? false);
    setButtonText(data.buttonText || 'Enquire Now');
    setFormTitle(data.formTitle || 'Request a Call Back');
    setPrimaryColor(data.primaryColor || '#2563eb');
    setAllowedDomainsInput(Array.isArray(data.allowedDomains) ? data.allowedDomains.join('\n') : '');
  };

  const loadIntegrationSettings = async () => {
    setLoadingIntegration(true);
    try {
      const res = await getWebsiteLeadIntegration();
      if (res && res.data) {
        updateLocalState(res.data);
      }
    } catch (err) {
      showToast(err.message || 'Failed to load integration settings', 'error');
    } finally {
      setLoadingIntegration(false);
    }
  };

  useEffect(() => {
    if (tabValue === 1 && !integrationData) {
      loadIntegrationSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  // Fetch website leads on tab mount and state changes
  useEffect(() => {
    if (tabValue === 0) {
      const delayDebounce = setTimeout(() => {
        fetchWebsiteLeads();
      }, 350);
      return () => clearTimeout(delayDebounce);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, tabValue]);

  const handleSaveSettings = async () => {
    setSavingIntegration(true);
    try {
      const domains = allowedDomainsInput
        .split(/[\n,]+/)
        .map(d => d.trim())
        .filter(d => d.length > 0);
      
      const payload = {
        isActive,
        buttonText,
        formTitle,
        primaryColor,
        allowedDomains: domains
      };
      
      const res = await updateWebsiteLeadIntegration(payload);
      if (res && res.data) {
        updateLocalState(res.data);
        showToast('Integration settings updated successfully!', 'success');
      }
    } catch (err) {
      showToast(err.message || 'Failed to save settings', 'error');
    } finally {
      setSavingIntegration(false);
    }
  };

  const handleConfirmRegenerate = async () => {
    setShowRegenConfirm(false);
    setRegenerating(true);
    try {
      const res = await regenerateWebsiteLeadKey();
      if (res && res.data) {
        updateLocalState(res.data);
        showToast('API Key regenerated successfully. Old scripts are now invalidated.', 'success');
      }
    } catch (err) {
      showToast(err.message || 'Failed to regenerate API key', 'error');
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    showToast(`${label} copied to clipboard!`, 'success');
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <WebIcon sx={{ color: '#0ea5e9', fontSize: 32 }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.5px' }}>
              Website Leads
            </Typography>
            <Chip 
              label="Live Webhook Active" 
              sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700, fontSize: '0.75rem', height: '24px' }} 
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            View organic leads submitted via website contact forms and configure direct Webhook/API integrations.
          </Typography>
        </Box>

        {tabValue === 0 && (
          <Button
            variant="outlined"
            onClick={handleRefresh}
            startIcon={<RefreshIcon />}
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, bgcolor: 'white', borderColor: '#e2e8f0', color: '#475569' }}
          >
            Refresh Feed
          </Button>
        )}
      </Box>

      {/* Tabs Menu */}
      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        sx={{
          mb: 4,
          borderBottom: '1px solid #e2e8f0',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.95rem',
            color: '#64748b',
          },
          '& .Mui-selected': {
            color: '#2563eb !important',
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#2563eb',
          }
        }}
      >
        <Tab label="Website Leads" />
        <Tab label="Website Lead Integration" />
      </Tabs>

      {/* Tab 1: Leads Feed */}
      {tabValue === 0 && (
        <Box>
          {/* KPI Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4} md={4}>
              <Card sx={{ borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #0ea5e9' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Total Web Leads
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="#1e293b">
                    {totalLeads}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <Card sx={{ borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #10b981' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Today's Submissions
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="#1e293b">
                    {leads.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <Card sx={{ borderRadius: 4, border: '1px solid #e2e8f0', borderLeft: '4px solid #6366f1' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Integrated Forms
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="#1e293b">
                    Active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
          </Grid>

          {/* Main split grid */}
          <Grid container spacing={4}>
            {/* Left Side: Leads Feed */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.015)', overflow: 'hidden' }}>
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h6" fontWeight={800} color="#1e293b">
                    Recent Submissions
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Search leads..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: { xs: '100%', sm: 260 },
                      '& .MuiOutlinedInput-root': { borderRadius: 3 },
                    }}
                  />
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, color: '#64748b' }}>Lead Name</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#64748b' }}>Contact Detail</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#64748b' }}>Interest / URL</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#64748b' }}>Assigned To</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#64748b' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#64748b' }}>Submitted At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leadsLoading ? (
                        [1, 2, 3].map((i) => (
                          <TableRow key={i}>
                            <TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell>
                          </TableRow>
                        ))
                      ) : leads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                            No website leads match the search criteria.
                          </TableCell>
                        </TableRow>
                      ) : (
                        leads.map((lead) => {
                          const name = lead.customerDetails?.name || lead.name || 'Unknown';
                          const email = lead.customerDetails?.email || lead.email || '—';
                          const phone = lead.customerDetails?.phone || lead.phone || '—';
                          const interest = lead.interestedIn || '—';
                          const status = lead.associateId ? 'Assigned' : 'New';
                          const submittedAt = lead.createdAt || lead.submittedAt;
                          const assignedTo = lead.associateDetails?.name || '-';

                          return (
                            <TableRow key={lead._id || lead.id} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700 }}>
                                    {name.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight={700} color="#1e293b">
                                      {name}
                                    </Typography>
                                    {/* <Typography variant="caption" color="text.secondary">
                                      {(lead._id || lead.id || '').toUpperCase()}
                                    </Typography> */}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="#334155">{email}</Typography>
                                <Typography variant="caption" color="text.secondary">{phone}</Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <FormIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                  <Typography variant="body2" fontWeight={600} color="#334155">{interest}</Typography>
                                </Box>
                                {/* <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={pageUrl}>
                                  {pageUrl}
                                </Typography> */}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600} color="#475569">{assignedTo}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={status}
                                  size="small"
                                  color={status === 'New' ? 'primary' : 'success'}
                                  sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="#475569">
                                  {submittedAt ? new Date(submittedAt).toLocaleString() : '—'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={totalLeads}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 20]}
                />
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Tab 2: Website Lead Integration */}
      {tabValue === 1 && (
        <Box>
          {loadingIntegration ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={4}>
              {/* Left Column: Config settings */}
              <Grid item xs={12} lg={7}>
                <Card sx={{ borderRadius: 4, border: '1px solid #e2e8f0', p: 1 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Box>
                        <Typography variant="h6" fontWeight={800} color="#1e293b">
                          Widget & Form Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Configure the widget button styling, lead form header title, and allowed origin domains.
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography fontWeight={700} color="#1e293b">
                            {isActive ? 'Active' : 'Inactive'}
                          </Typography>
                        }
                      />
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 3, border: '1px dashed #cbd5e1', mb: 1 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                                Company Name
                              </Typography>
                              <Typography variant="body1" fontWeight={700} color="#334155">
                                {integrationData?.companyName || '-'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                                Company Key
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="#64748b" sx={{ wordBreak: 'break-all', fontFamily: 'Courier, monospace' }}>
                                {integrationData?.companyKey || '-'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Button Text"
                          fullWidth
                          size="small"
                          value={buttonText}
                          onChange={(e) => setButtonText(e.target.value)}
                          helperText="Floating action button text (e.g. 'Enquire Now')"
                          InputProps={{ style: { borderRadius: 8 } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Form Title"
                          fullWidth
                          size="small"
                          value={formTitle}
                          onChange={(e) => setFormTitle(e.target.value)}
                          helperText="Form header title (e.g. 'Request a Call Back')"
                          InputProps={{ style: { borderRadius: 8 } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Primary Color"
                          fullWidth
                          size="small"
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                          helperText="Choose the theme color for the widget button"
                          InputProps={{
                            style: { borderRadius: 8 },
                            startAdornment: (
                              <InputAdornment position="start">
                                <input
                                  type="color"
                                  value={primaryColor}
                                  onChange={(e) => setPrimaryColor(e.target.value)}
                                  style={{
                                    border: 'none',
                                    background: 'none',
                                    width: '24px',
                                    height: '24px',
                                    cursor: 'pointer',
                                    padding: 0,
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Allowed Domains"
                          fullWidth
                          multiline
                          rows={3}
                          value={allowedDomainsInput}
                          onChange={(e) => setAllowedDomainsInput(e.target.value)}
                          placeholder="https://example.com&#10;https://www.example.com"
                          helperText="Allowed domains list, one per line or separated by commas"
                          InputProps={{ style: { borderRadius: 8 } }}
                        />
                      </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setShowRegenConfirm(true)}
                        disabled={regenerating || savingIntegration}
                        sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
                      >
                        {regenerating ? 'Regenerating...' : 'Regenerate Key'}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSaveSettings}
                        disabled={savingIntegration || regenerating}
                        sx={{
                          borderRadius: 3,
                          textTransform: 'none',
                          fontWeight: 700,
                          backgroundColor: '#2563eb',
                          boxShadow: 'none',
                          '&:hover': { backgroundColor: '#1d4ed8', boxShadow: 'none' },
                        }}
                      >
                        {savingIntegration ? 'Saving...' : 'Save Settings'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right Column: Code snippets & deployment instructions */}
              <Grid item xs={12} lg={5}>
                <Card sx={{ borderRadius: 4, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight={800} color="#1e293b" gutterBottom>
                      Widget Integration Code
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Paste the following script tag directly into your website's HTML before the closing <code>&lt;/head&gt;</code> or <code>&lt;/body&gt;</code> tag.
                    </Typography>

                    {/* Script snippet codeblock */}
                    <Box
                      sx={{
                        position: 'relative',
                        bgcolor: '#0f172a',
                        borderRadius: 3,
                        p: 2.5,
                        mb: 3,
                        fontFamily: 'Courier, monospace',
                        fontSize: '0.825rem',
                        color: '#f8fafc',
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        border: '1px solid #334155',
                      }}
                    >
                      <Tooltip title="Copy Script Code" placement="left">
                        <IconButton
                          onClick={() => handleCopyToClipboard(integrationData?.scriptCode, 'Script Code')}
                          size="small"
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: '#94a3b8',
                            '&:hover': { color: '#f8fafc', bgcolor: '#1e293b' },
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {integrationData?.scriptCode || '// Loading script code...'}
                    </Box>

                    {/* Public Form Preview Button */}
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        if (integrationData?.publicFormUrl) {
                          window.open(integrationData.publicFormUrl, '_blank');
                        } else {
                          showToast('Public form URL not available', 'error');
                        }
                      }}
                      disabled={!integrationData?.publicFormUrl}
                      sx={{
                        mt: 'auto',
                        py: 1.25,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        borderColor: '#2563eb',
                        color: '#2563eb',
                        '&:hover': { borderColor: '#1d4ed8', bgcolor: 'rgba(37, 99, 235, 0.04)' },
                      }}
                    >
                      Public Form Preview
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* Confirmation modal for Regenerate Key */}
      <ConfirmModal
        open={showRegenConfirm}
        title="Regenerate API Key"
        type="warning"
        confirmText="Regenerate"
        cancelText="Cancel"
        onClose={() => setShowRegenConfirm(false)}
        onConfirm={handleConfirmRegenerate}
      >
        Regenerating key will invalidate the old website script. The new script code must be pasted on the website.
      </ConfirmModal>

      {/* Snackbar alerts */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setToast((prev) => ({ ...prev, open: false }))} 
          severity={toast.severity} 
          sx={{ borderRadius: 3, fontWeight: 700 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Website;
